import { type ReactNode } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import styles from "@/routes/lms/lmsCourseOverview.module.css";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

interface Props {
  featuredImageUrl: string | null;
  imageAlt: string;
  title: string;
  onViewImage?: () => void;
  onEditImage?: () => void;
  canViewImage?: boolean;
  topRightContent?: ReactNode;
  children?: ReactNode;
}

export default function LmsCourseOverviewHeader({
  featuredImageUrl,
  imageAlt,
  title,
  onViewImage,
  onEditImage,
  canViewImage = true,
  topRightContent,
  children,
}: Props) {
  const { t } = useTranslation();
  usePageHeading(title);
  const showImageActions = Boolean(onViewImage || onEditImage);

  return (
    <div className={styles.headerContainer}>
      <div className={styles.headerImageContainer}>
        {featuredImageUrl ? (
          <img
            src={featuredImageUrl}
            alt={imageAlt}
            className={styles.headerFeaturedImage}
          />
        ) : (
          <div className={styles.headerFeaturedImagePlaceholder}>
            <InsertPhotoOutlinedIcon />
          </div>
        )}
        {showImageActions && (
          <div className={styles.headerFeaturedImageActions}>
            {onViewImage && (
              <Tooltip
                title={t("view")}
                placement="top"
                arrow
                enterDelay={1000}
                enterNextDelay={250}
              >
                <span>
                  <GenericButton
                    type="button"
                    variant="icon"
                    color="iconBase"
                    onClick={onViewImage}
                    disabled={!canViewImage}
                    className={styles.headerFeaturedImageActionButton}
                  >
                    <VisibilityOutlinedIcon />
                  </GenericButton>
                </span>
              </Tooltip>
            )}
            {onEditImage && (
              <Tooltip
                title={t("replace_featured_image")}
                placement="top"
                arrow
                enterDelay={1000}
                enterNextDelay={250}
              >
                <GenericButton
                  type="button"
                  variant="icon"
                  color="iconBase"
                  onClick={onEditImage}
                  className={styles.headerFeaturedImageActionButton}
                >
                  <EditOutlinedIcon />
                </GenericButton>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <div
        className={`
          ${styles.headerContentContainer}
          ${!topRightContent ? styles.headerContentContainerAlignEnd : ""}
        `}
      >
        {topRightContent && (
          <div className={styles.headerStatusContainer}>{topRightContent}</div>
        )}
        <div className={styles.headerTitleTabsContainer}>
          {children}
        </div>
      </div>
    </div>
  );
}
